import { useMemo, useState } from 'react';
import { atom, useAtom } from 'jotai';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import type { AgentApiMetaMessagesRecord } from '@/types/serializers';
import useFetchCandidateChatThreads, {
  type ChatThreadScope,
} from '../candidates/api/useFetchCandidateChatThreads';

// ヘッダーのメッセージ受信箱の開閉状態（ヘッダーのチャットボタンから制御する）
export const headerMessagePopupAtom = atom(false);

const TABS: { value: ChatThreadScope; label: string }[] = [
  { value: 'mine', label: '自分の求職者' },
  { value: 'all', label: '全ての求職者' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

// 最終メッセージの相対時刻表示（仕様）:
// 1分未満=たった今 / 1時間未満=〇分前 / 24時間未満=〇時間前 / 昨日=昨日
// / 2〜6日=〇日前 / 7〜29日=〇週間前 / 30日以上=〇ヶ月前
const formatRelativeTime = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'たった今';
  if (diffMin < 60) return `${diffMin}分前`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}時間前`;
  const diffDay = Math.floor(diffMs / DAY_MS);
  if (diffDay < 2) return '昨日';
  if (diffDay < 7) return `${diffDay}日前`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}週間前`;
  return `${Math.floor(diffDay / 30)}ヶ月前`;
};

function ScopeTabs({
  scope,
  onChange,
  unreadByScope,
}: {
  scope: ChatThreadScope;
  onChange: (scope: ChatThreadScope) => void;
  unreadByScope: Record<ChatThreadScope, boolean>;
}) {
  return (
    <div className="flex h-9.5 items-center">
      {TABS.map((tab, index) => {
        const isActive = scope === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={clsx(
              'flex h-full items-center justify-center gap-xxs border border-solid border-border px-xs text-body-xs whitespace-nowrap transition-colors',
              index === 0 ? 'rounded-l-sm' : 'rounded-r-sm border-l-0',
              isActive
                ? 'bg-primary_lighten font-bold text-primary'
                : 'bg-white font-normal text-sub hover:text-main'
            )}
          >
            {tab.label}
            {unreadByScope[tab.value] && (
              <span className="size-1.5 shrink-0 rounded-full bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// 候補者が設定したアバターを表示。未設定時、および画像の読み込みに失敗した場合
// （S3 presigned URL の失効など）はデフォルトアバターにフォールバックする
function Avatar({ url }: { url: string | null }) {
  // 読み込みに失敗した URL を記録する。url が変われば自動的に再試行される
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  const src = url && url !== erroredUrl ? url : '/agent/default-avatar.png';

  return (
    <img
      src={src}
      alt=""
      onError={() => setErroredUrl(url)}
      className="size-10 shrink-0 rounded-full object-cover"
    />
  );
}

function ThreadRow({
  thread,
  onNavigate,
}: {
  thread: AgentApiMetaMessagesRecord;
  onNavigate: () => void;
}) {
  return (
    <Link
      to="/agent/candidates/$candidateProfileId"
      params={{ candidateProfileId: String(thread.candidate_profile_id) }}
      search={{ tab: 'mypage' }}
      onClick={onNavigate}
      className="flex w-full items-center gap-md border-b-[0.5px] border-border px-md py-sm text-left hover:bg-gray"
    >
      <Avatar url={thread.avatar_url} />
      <div className="flex min-w-0 flex-1 flex-col gap-xxs">
        <div className="flex items-center gap-xs">
          <span className="flex-1 truncate text-body-sm font-bold text-main">
            {thread.candidate_profile_name}
          </span>
          <span className="shrink-0 text-body-xs whitespace-nowrap text-sub">
            {formatRelativeTime(thread.commented_at)}
          </span>
          {thread.is_unread && <span className="size-1.5 shrink-0 rounded-full bg-primary" />}
        </div>
        <span className="truncate text-body-sm text-sub">{thread.last_message_preview ?? ''}</span>
      </div>
    </Link>
  );
}

// ローディング中に表示するスケルトン行（アバター円＋2本のバー）
function SkeletonRow() {
  return (
    <div className="flex items-center gap-md border-b-[0.5px] border-border px-md py-sm">
      <div className="size-10 shrink-0 rounded-full bg-gray-100" />
      <div className="flex flex-1 flex-col gap-xs">
        <div className="h-3 w-2/5 rounded bg-gray-100" />
        <div className="h-3 w-4/5 rounded bg-gray-100" />
      </div>
    </div>
  );
}

function EmptyState({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-xs px-xl text-center">
      <p className="text-heading-xs text-main">メッセージがありません</p>
      <p className="text-body-sm leading-6 text-sub">
        <Link to="/agent/candidates" onClick={onNavigate} className="text-primary underline">
          求職者管理
        </Link>
        から求職者を選択して
        <br />
        メッセージを開始してください。
      </p>
    </div>
  );
}

export default function HeaderMessagePopup() {
  const [isOpen, setIsOpen] = useAtom(headerMessagePopupAtom);
  const [scope, setScope] = useState<ChatThreadScope>('mine');

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useFetchCandidateChatThreads(scope, isOpen);

  // タブごとの未読バッジは、選択中タブに関わらず各タブ自身の未読状態で表示する
  // （別タブに切り替えても未読があれば青丸を残す）
  const { data: mineData } = useFetchCandidateChatThreads('mine', isOpen);
  const { data: allData } = useFetchCandidateChatThreads('all', isOpen);
  const unreadByScope: Record<ChatThreadScope, boolean> = {
    mine: mineData?.pages[0]?.has_unread ?? false,
    all: allData?.pages[0]?.has_unread ?? false,
  };

  const threads = useMemo(() => data?.pages.flatMap((page) => page.records) ?? [], [data]);

  if (!isOpen) return null;

  const close = () => setIsOpen(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 80 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-9998" onClick={close} />
      <div className="absolute top-full right-lg z-9999 flex h-138.5 max-h-[80vh] w-103.75 flex-col overflow-hidden rounded-sm bg-white shadow-layer3">
        <div className="flex items-center justify-between gap-sm border-b border-border px-md py-sm">
          <span className="text-heading-xs text-main">メッセージ</span>
          <ScopeTabs scope={scope} onChange={setScope} unreadByScope={unreadByScope} />
        </div>
        {isLoading ? (
          <div className="hidden-scrollbar flex min-h-0 flex-1 animate-pulse flex-col overflow-y-auto overscroll-contain">
            {Array.from({ length: 8 }).map((_, index) => (
              <SkeletonRow key={index} />
            ))}
          </div>
        ) : threads.length === 0 ? (
          <EmptyState onNavigate={close} />
        ) : (
          <div
            onScroll={handleScroll}
            className="hidden-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
          >
            {threads.map((thread) => (
              <ThreadRow key={thread.candidate_profile_id} thread={thread} onNavigate={close} />
            ))}
            {isFetchingNextPage && (
              <div className="flex justify-center py-md">
                <div className="size-8 animate-spin rounded-full border-2 border-solid border-border border-t-transparent" />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
